import {
	Button,
	Row,
	Result,
	Select,
	Switch,
} from "antd";

import { UploadDocument } from '@qualitas/upload-document';

import FormLayout from "../../FormLayout/FormLayout";

import { ContractAPI } from "../../../Api/ContractAPI";
import { InvestorAPI } from "../../../Api/InvestorAPI";
import { DocumentsAPI } from "../../../Api/DocumentsAPI";
import { BeneficiaryAPI } from "../../../Api/BeneficiaryAPI";
import { RepresentativesAPI } from "../../../Api/RepresentativesAPI";

import { useLegalInvestorDocuments } from "../../../Hooks/useLegalFormDocuments/useLegalInvestorDocuments";

import { ILegalInvestorDocuments } from "./ILegalInvestorDocuments";
import { Beneficiaries, Representative } from "../../../Models";

import { FileTypes } from "../../../Assets/Enums";
import { getLocalizedLegalSourceValue } from "../../../Assets/OptionsTranslations";

import "./LegalInvestorDocuments.scss";

const LegalInvestorDocuments = ({
	baseURL,
	baseURLExternal,
	translations,
	language,
	clientId,
	userIdInstitution,
	unlockNextStep,
	moduleRole,
	isReadOnly = false,
}: ILegalInvestorDocuments) => {
	const contractAPI = ContractAPI(baseURL, baseURLExternal, clientId);
	const investorsAPI = InvestorAPI(baseURL, baseURLExternal, clientId);
	const beneficiariesAPI = BeneficiaryAPI(baseURL, clientId);
	const representativesAPI = RepresentativesAPI(baseURL, clientId);
	const documentsAPI = DocumentsAPI(baseURL, clientId);
	
	const {
		loading,
		noContent,
		contractData,
		investors,
		beneficiaries,
		representatives,
		contractDocuments,
		errors,
		handleUpdateIdDocument,
		handleUpdateFundSourceDocument,
		handleUpdateDocument,
		saveData,
		areAllDocumentsUploaded,
		getFundsOriginOptions,
		handleDocumentTypeChange,
		showSecondDocument,
		setShowSecondDocument,
		isTaxIdentificationCardMandatory,
		isMandatoryMainDocumentMissing,
		isMandatoryTaxResidenceDocumentMissing,
		isMandatoryFundSourceDocumentMissing,
		isMandatoryPersonDocumentMissing,
		isPersonDocumentMandatory,
	} = useLegalInvestorDocuments({
		translations,
		contractAPI,
		investorsAPI,
		documentsAPI,
		beneficiariesAPI,
		representativesAPI,
		userIdInstitution,
		unlockNextStep,
	});

	const isRepresentativesDocumentSwitchDisabled = !contractDocuments.articlesOfIncorporation?.url;
	const isPortugueseTaxResidence = investors.length > 0 && investors[0].countryTaxResidence === "Portugal";

	return (
		<>
			{!noContent ? (
				<>
					<FormLayout
						translations={translations}
						loading={loading}
						title={translations.documentsTitle}
						subTitle={translations.documentsSubtitle}
						onClick={saveData}
						contractId={contractData.id}
						nextStep={unlockNextStep}
						enableNextStep={areAllDocumentsUploaded()}
						forceEnableNextStep={isReadOnly}
						confirm={contractData.id ? null : translations.confirmContract }
						disabledSaveButton={false}
						hideSaveButton={isReadOnly}
					>
						<Row className="documents-list-legal full-width">
							<div className="subitem-line">
							<Row justify="start" className="documents-row full-width">
								<UploadDocument
									baseURL={baseURL}
									clientId={clientId}
									moduleRole={moduleRole}
									translations={{
										label: translations.escrituraConst,
										uploadDocument: translations.uploadDocument,
										uploadDocumentTooltip: translations.allowedFileFormats,
										selectFiles: translations.selectFiles,
										changeFile: translations.changeFile,
									}}
									mandatory={true}
									oneRow={false}
									uploadedFile={contractDocuments.articlesOfIncorporation?.url}
									disabled={isReadOnly}
									error={isMandatoryMainDocumentMissing('articlesOfIncorporation')}
									shouldRunOcr={false}
									ocrFields={[]}
									updateFileUrl={handleUpdateDocument("articlesOfIncorporation")}
								/>
							</Row>
							<Row justify="start" className="documents-row full-width" style={{ marginBottom: '16px' }}>
								<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
									<span>{translations.copiaEscritura}</span>
									<Switch 
										disabled={isReadOnly || isRepresentativesDocumentSwitchDisabled}
										checked={showSecondDocument}
										onChange={setShowSecondDocument}
									/>
								</div>
							</Row>
							{showSecondDocument
								? <Row justify="start" className="documents-row full-width">
									<UploadDocument
										baseURL={baseURL}
										clientId={clientId}
										moduleRole={moduleRole}
										translations={{
											label: translations.copiaEscritura,
											uploadDocument: translations.uploadDocument,
											uploadDocumentTooltip: translations.allowedFileFormats,
											selectFiles: translations.selectFiles,
											changeFile: translations.changeFile,
										}}
										mandatory={true}
										oneRow={false}
										uploadedFile={contractDocuments.representativesDocumentFile?.url}
										disabled={isReadOnly}
										error={isMandatoryMainDocumentMissing('representativesDocumentFile')}
										shouldRunOcr={false}
										ocrFields={[]}
										updateFileUrl={handleUpdateDocument("representativesDocumentFile")}
									/>
								</Row>
								: null
							}
							<Row justify="start" className="documents-row full-width">
									<UploadDocument
										baseURL={baseURL}
										clientId={clientId}
										moduleRole={moduleRole}
										translations={{
											label: translations.copiaEstatutos,
											uploadDocument: translations.uploadDocument,
											uploadDocumentTooltip: translations.allowedFileFormats,
											selectFiles: translations.selectFiles,
											changeFile: translations.changeFile,
										}}
										mandatory={false}
										oneRow={false}
										uploadedFile={contractDocuments.statutesOrRegulations?.url}
										disabled={isReadOnly}
										error={false}
										shouldRunOcr={false}
										ocrFields={[]}
										updateFileUrl={handleUpdateDocument("statutesOrRegulations")}
									/>
								</Row>
								<Row justify="start" className="documents-row full-width">
									<UploadDocument
										baseURL={baseURL}
										clientId={clientId}
										moduleRole={moduleRole}
										translations={{
											label: translations.estatutos,
											uploadDocument: translations.uploadDocument,
											uploadDocumentTooltip: translations.allowedFileFormats,
											selectFiles: translations.selectFiles,
											changeFile: translations.changeFile,
										}}
										mandatory={false}
										oneRow={false}
										uploadedFile={contractDocuments.statutesModification?.url}
										disabled={isReadOnly}
										error={false}
										shouldRunOcr={false}
										ocrFields={[]}
										updateFileUrl={handleUpdateDocument("statutesModification")}
									/>
								</Row>
								<Row justify="start" className="documents-row full-width">
									<UploadDocument
										baseURL={baseURL}
										clientId={clientId}
										moduleRole={moduleRole}
										translations={{
											label: translations.representativePowerOfAttorney,
											uploadDocument: translations.uploadDocument,
											uploadDocumentTooltip: translations.allowedFileFormats,
											selectFiles: translations.selectFiles,
											changeFile: translations.changeFile,
										}}
										mandatory={false}
										oneRow={false}
										uploadedFile={contractDocuments.representativePowerOfAttorney?.url}
										disabled={isReadOnly}
										error={false}
										shouldRunOcr={false}
										ocrFields={[]}
										updateFileUrl={handleUpdateDocument("representativePowerOfAttorney")}
									/>
								</Row>
								<Row justify="start" className="documents-row full-width">
									<UploadDocument
										baseURL={baseURL}
										clientId={clientId}
										moduleRole={moduleRole}
										translations={{
											label: translations.detallesRegistro,
											uploadDocument: translations.uploadDocument,
											uploadDocumentTooltip: translations.allowedFileFormats,
											selectFiles: translations.selectFiles,
											changeFile: translations.changeFile,
										}}
										mandatory={false}
										oneRow={false}
										uploadedFile={contractDocuments.regulatedEntityRegistry?.url}
										disabled={isReadOnly}
										error={false}
										shouldRunOcr={false}
										ocrFields={[]}
										updateFileUrl={handleUpdateDocument("regulatedEntityRegistry")}
									/>
								</Row>
								<Row justify="start" className="documents-row full-width">
									<UploadDocument
										baseURL={baseURL}
										clientId={clientId}
										moduleRole={moduleRole}
										translations={{
											label: translations.tarjetaNIF,
											uploadDocument: translations.uploadDocument,
											uploadDocumentTooltip: translations.allowedFileFormats,
											selectFiles: translations.selectFiles,
											changeFile: translations.changeFile,
										}}
										mandatory={isTaxIdentificationCardMandatory}
										oneRow={false}
										uploadedFile={contractDocuments.taxIdentificationCard?.url}
										disabled={isReadOnly}
										error={isMandatoryMainDocumentMissing('taxIdentificationCard')}
										shouldRunOcr={false}
										ocrFields={[]}
										updateFileUrl={handleUpdateDocument("taxIdentificationCard")}
									/>
								</Row>
								<Row justify="start" className="documents-row full-width">
									<UploadDocument
										baseURL={baseURL}
										clientId={clientId}
										moduleRole={moduleRole}
										translations={{
											label: translations.beneficialOwnershipCertificate,
											uploadDocument: translations.uploadDocument,
											uploadDocumentTooltip: translations.allowedFileFormats,
											selectFiles: translations.selectFiles,
											changeFile: translations.changeFile,
										}}
										mandatory={true}
										oneRow={false}
										uploadedFile={contractDocuments?.beneficialOwnershipCertificate?.url}
										disabled={isReadOnly}
										error={isMandatoryMainDocumentMissing('beneficialOwnershipCertificate')}
										shouldRunOcr={false}
										ocrFields={[]}
										updateFileUrl={handleUpdateDocument("beneficialOwnershipCertificate")}
									/>
								</Row>
								<Row justify="start" className="documents-row full-width">
									<UploadDocument
										baseURL={baseURL}
										clientId={clientId}
										moduleRole={moduleRole}
										translations={{
											label: translations.bankCertificate,
											uploadDocument: translations.uploadDocument,
											uploadDocumentTooltip: translations.allowedFileFormats,
											selectFiles: translations.selectFiles,
											changeFile: translations.changeFile,
										}}
										mandatory={true}
										oneRow={false}
										uploadedFile={contractDocuments?.bankCertificate?.url}
										disabled={isReadOnly}
										error={isMandatoryMainDocumentMissing('bankCertificate')}
										shouldRunOcr={false}
										ocrFields={[]}
										updateFileUrl={handleUpdateDocument("bankCertificate")}
									/>
								</Row>
								{
									investors.length > 0 && investors[0].countryTaxResidence !== "España" 
										? (
											<>
												{
													!investors[0].american
														? (
															<Row className="documents-row full-width">
																<UploadDocument
																	baseURL={baseURL}
																	clientId={clientId}
																	moduleRole={moduleRole}
																	translations={{
																		label: translations.formW8ShortEntities,
																		uploadDocument: translations.uploadDocument,
																		uploadDocumentTooltip: translations.allowedFileFormats,
																		selectFiles: translations.selectFiles,
																		changeFile: translations.changeFile,
																	}}
																	mandatory={true}
																	oneRow={false}
																	uploadedFile={contractDocuments.w8BEN?.url}
																	disabled={isReadOnly}
																	error={isMandatoryTaxResidenceDocumentMissing('w8BEN')}
																	shouldRunOcr={false}
																	ocrFields={[]}
																	updateFileUrl={handleUpdateDocument('w8BEN')}
																/>
															</Row>
														) : <Row className="documents-row full-width">
																<UploadDocument
																	baseURL={baseURL}
																	clientId={clientId}
																	moduleRole={moduleRole}
																	translations={{
																		label: translations.formW9Short,
																		uploadDocument: translations.uploadDocument,
																		uploadDocumentTooltip: translations.allowedFileFormats,
																		selectFiles: translations.selectFiles,
																		changeFile: translations.changeFile,
																	}}
																	mandatory={true}
																	oneRow={false}
																	uploadedFile={contractDocuments.w9?.url}
																	disabled={isReadOnly}
																	error={isMandatoryTaxResidenceDocumentMissing('w9')}
																	shouldRunOcr={false}
																	ocrFields={[]}
																	updateFileUrl={handleUpdateDocument('w9')}
																/>
															</Row>
												}
												<Row className="documents-row full-width">
													<UploadDocument
														baseURL={baseURL}
														clientId={clientId}
														moduleRole={moduleRole}
														translations={{
															label: translations.tin,
															uploadDocument: translations.uploadDocument,
															uploadDocumentTooltip: translations.allowedFileFormats,
															selectFiles: translations.selectFiles,
															changeFile: translations.changeFile,
														}}
														mandatory={false}
														oneRow={false}
														uploadedFile={contractDocuments.tin?.url}
														disabled={isReadOnly}
														error={false}
														shouldRunOcr={false}
														ocrFields={[]}
														updateFileUrl={handleUpdateDocument('tin')}
													/>
												</Row>
												<Row className="documents-row full-width">
													<UploadDocument
														baseURL={baseURL}
														clientId={clientId}
														moduleRole={moduleRole}
														translations={{
															label: translations.taxResidenceCertificate,
															uploadDocument: translations.uploadDocument,
															uploadDocumentTooltip: translations.allowedFileFormats,
															selectFiles: translations.selectFiles,
															changeFile: translations.changeFile,
														}}
														mandatory={!isPortugueseTaxResidence}
														oneRow={false}
														uploadedFile={contractDocuments.taxResidenceCertificate?.url}
														disabled={isReadOnly}
														error={isMandatoryTaxResidenceDocumentMissing('taxResidenceCertificate')}
														shouldRunOcr={false}
														ocrFields={[]}
														updateFileUrl={handleUpdateDocument('taxResidenceCertificate')}
													/>
												</Row>
											</>
										) : null
								}
							</div>
						</Row>
						<Row className="documents-list-legal full-width">
							<div className="subitem-line">
								<h3>{translations.fundSourcesPJTitle}</h3>
								{
									(investors.length > 0 && investors[0].source ? investors[0].source.split(';') : []).map((rawSource: string, sourceIndex: number) => {
										const source = rawSource.trim();
										if (!source) {
											return null;
										}
										const localizedSource = getLocalizedLegalSourceValue(source, language);
										const fundsOriginType = FileTypes[source] || source;
										const contractErrors = errors?.contract || {};
										const sourceErrors = contractErrors[fundsOriginType] || contractErrors[source] || {};
										const fundsOriginDoc = contractDocuments?.fundsOriginDocuments?.find(
											(doc) => doc.type === fundsOriginType || doc.type === source
										) || null;
										return (
											<Row justify="space-between" className="documents-row" key={`legal-funds-source-${source}-${sourceIndex}`}>
												<UploadDocument
													className="document-uploader"
													baseURL={baseURL}
													clientId={clientId}
													moduleRole={moduleRole}
													translations={{
														label: localizedSource,
														uploadDocument: translations.uploadDocument,
														uploadDocumentTooltip: translations.allowedFileFormats,
														selectFiles: translations.selectFiles,
														changeFile: translations.changeFile,
													}}
													mandatory={true}
													oneRow={false}
													uploadedFile={fundsOriginDoc?.url}
													disabled={isReadOnly}
													error={Boolean(sourceErrors.fundsOriginFileNotUploaded || isMandatoryFundSourceDocumentMissing(source))}
													shouldRunOcr={false}
													ocrFields={[]}
													updateFileUrl={handleUpdateFundSourceDocument(source)}
													fileName={`${translations.fundSourcesPJTitle.replace(' ', '-')}_${source.replace(' ', '-')}`}
													maxDocuments={3}
													allowMultipleDocuments
												/>
												<div className="document-type-select">
													<label className="required">
														{translations.documentType}
													</label>
													<Select
														style={{ width: "100%" }}
														showSearch
														allowClear={false}
														value={fundsOriginDoc?.description || undefined}
														maxTagCount="responsive"
														onChange={handleDocumentTypeChange(source)}
														status={sourceErrors.fundsOriginTypeNotSelected ? 'error' : undefined}
														options={getFundsOriginOptions(source)}
														disabled={isReadOnly || !fundsOriginDoc?.url}
													/>
												</div>
											</Row>
										);
									})
								}
							</div>
						</Row>
						{ representatives.map((representative: Representative, index: number) => (
							<Row className="documents-list-legal full-width">
								<div className="subitem-line">
									<h3>
										{translations.representativeAttorneyData} {representative.name} {representative.lastName} {representative.secondLastName}
									</h3>
									<Row justify="start" className="documents-row full-width">
										<UploadDocument
											baseURL={baseURL}
											clientId={clientId}
											moduleRole={moduleRole}
										translations={{
											label: translations[representative.documentType?.toLowerCase() as keyof typeof translations] || representative.documentType,
												uploadDocument: translations.uploadDocument,
												uploadDocumentTooltip: translations.allowedFileFormats,
												selectFiles: translations.selectFiles,
												changeFile: translations.changeFile,
											}}
											mandatory={isPersonDocumentMandatory('Representative', index, 'idDocument')}
											oneRow={false}
											uploadedFile={
												contractDocuments.representativesIdDocuments.find((doc) => Number(doc.idRegistry) === Number(representative.id))?.url
												|| (Number(contractDocuments.representativesIdDocuments[index]?.idRegistry || 0) <= 0
													? contractDocuments.representativesIdDocuments[index]?.url
													: "")
												|| ""
											}
											disabled={isReadOnly}
											error={isMandatoryPersonDocumentMissing('Representative', index, 'idDocument')}
											shouldRunOcr={false}
											ocrFields={[]}
											updateFileUrl={handleUpdateIdDocument(index, representative.documentType, "Representative")}
											fileName={`${translations[representative.documentType?.toLowerCase() as keyof typeof translations]?.replace(' ', '-')}`}
											maxDocuments={3}
											allowMultipleDocuments
										/>
									</Row>
								</div>
							</Row>
						))}
						<Row className="documents-list-legal full-width">
							{ beneficiaries.map((beneficiary: Beneficiaries, index: number) => (
								<div className="subitem-line">
									<h3>
										{translations.beneficiaryData} {beneficiary.name} {beneficiary.lastName} {beneficiary.secondLastName}
									</h3>
									<Row justify="start" className="documents-row full-width">
										<UploadDocument
											baseURL={baseURL}
											clientId={clientId}
											moduleRole={moduleRole}
										translations={{
											label: translations[beneficiary.documentType?.toLowerCase() as keyof typeof translations] || beneficiary.documentType,
												uploadDocument: translations.uploadDocument,
												uploadDocumentTooltip: translations.allowedFileFormats,
												selectFiles: translations.selectFiles,
												changeFile: translations.changeFile,
											}}
											mandatory={isPersonDocumentMandatory('Beneficiary', index, 'idDocument')}
											oneRow={false}
											uploadedFile={
												contractDocuments.beneficiariesIdDocuments.find((doc) => Number(doc.idRegistry) === Number(beneficiary.id))?.url
												|| (Number(contractDocuments.beneficiariesIdDocuments[index]?.idRegistry || 0) <= 0
													? contractDocuments.beneficiariesIdDocuments[index]?.url
													: "")
												|| ""
											}
											disabled={isReadOnly}
											error={isMandatoryPersonDocumentMissing('Beneficiary', index, 'idDocument')}
											shouldRunOcr={false}
											ocrFields={[]}
											updateFileUrl={handleUpdateIdDocument(index, beneficiary.documentType, "Beneficiary")}
											fileName={`${translations[beneficiary.documentType?.toLowerCase() as keyof typeof translations]?.replace(' ', '-')}`}
											maxDocuments={3}
											allowMultipleDocuments
										/>
									</Row>
								</div>
							))}
						</Row>
					</FormLayout>
				</>
			) : (
				<div className="no-content-wrapper">
					<Result
						status="warning"
						title={translations.messageContrato}
						extra={
							<Button type="primary" key="console">
								{translations.inicio}
							</Button>
						}
					/>
				</div>
			)}
		</>
	);
};

export default LegalInvestorDocuments;
